import React, { useState, useEffect, useRef } from 'react';
import Card from '../../components/card/index.jsx';
import CustomSelect from '../../components/select-box/index.jsx';
import Tabs from '../../components/tabs/index.jsx';
import { headings, countries } from './helper.jsx';
import { getProductByLanguage } from '../../api/index';
import mainLogo from '../../../src/assets/logo.png';
import pdfImage from '../../assets/pdf.png';
import style from './main-page.module.scss';
import tabsStyle from '../../components/tabs/tabs.module.scss';
import VideoPlayer from '../../components/video-player.jsx';

const Bio = () => {
  const [productData, setProductData] = useState({});
  const [language, setLanguage] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [expandSustainability, setExpandSustainability] = useState(true);
  const sustainabilityRef = useRef(null);

  const getProductHandler = async (productId, language) => {
    const data = await getProductByLanguage(productId, language);
    setProductData(data?.productData || {});
    setDataLoaded(true);
  };

  const getHeadingText = (type) => {
    const selectedLanguage = headings.find(
      (item) => item.language === productData?.safetyDataSheet?.language
    );
    return selectedLanguage && selectedLanguage?.[type]
      ? selectedLanguage[type]
      : '';
  };

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const productId = pathParts[3];
    const initialLanguage = pathParts[4];

    setLanguage(initialLanguage);
    getProductHandler(productId, initialLanguage);
  }, []);

  const handleLanguageChange = (selectedOption) => {
    const newLanguage = selectedOption.value;
    setLanguage(selectedOption);

    const pathParts = window.location.pathname.split('/');
    const productId = pathParts[3];

    window.history.pushState(
      {},
      '',
      `/products/products-detail/${productId}/${newLanguage}`
    );

    getProductHandler(productId, newLanguage);
  };

  const calculateTotalScore = () => {
    if (!productData?.sustainability) return 0;

    const scores = [
      productData?.sustainability?.inhaltsstoffe?.score || 0,
      productData?.sustainability?.gesundheitUndSicherheit?.score || 0,
      productData?.sustainability?.ergiebigkeit?.score || 0,
      productData?.sustainability?.verpackungUndVersand?.score || 0,
      productData?.sustainability?.zertifizierungen?.score || 0,
    ];

    return scores.reduce((total, score) => total + score, 0);
  };

  const renderSustainabilityCategory = (
    category,
    score,
    maxScore,
    descriptions,
    isLast,
    clickLink
  ) => {
    if (!productData?.sustainability) return null;
    if (score === null && (!descriptions || descriptions.length === 0))
      return null;

    const getIconClass = (category) => {
      switch (category) {
        case 'inhaltsstoffe':
          return style.inhaltsstoffeIcon;
        case 'gesundheitUndSicherheit':
          return style.gesundheitIcon;
        case 'ergiebigkeit':
          return style.ergiebigkeitIcon;
        case 'verpackungUndVersand':
          return style.verpackungIcon;
        case 'zertifizierungen':
          return style.zertifizierungenIcon;
        default:
          return '';
      }
    };

    // Find description for current language
    const currentLanguage = productData?.safetyDataSheet?.language;
    let descriptionText = null;

    if (descriptions && descriptions.length > 0) {
      const languageDescription = descriptions.find(
        (desc) => desc.language === currentLanguage
      );
      descriptionText = languageDescription
        ? languageDescription.description
        : null;

      // If no description for current language, try to find a fallback
      if (!descriptionText) {
        // Try German as fallback
        const germanDesc = descriptions.find(
          (desc) => desc.language === 'de_DE'
        );
        if (germanDesc) {
          descriptionText = germanDesc.description;
        } else if (descriptions[0]?.description) {
          // Use the first available description as last resort
          descriptionText = descriptions[0].description;
        }
      }
    }

    return (
      <div className={style.sustainabilityCategory}>
        <div className={style.categoryHeader}>
          <div
            className={`${style.categoryIcon} ${getIconClass(category)}`}
          ></div>
          <h3 className={style.categoryTitle} onClick={clickLink}>
            {getHeadingText(category)}
          </h3>
          <div className={style.categoryScore}>
            {score !== null
              ? `${score}/${maxScore} ${getHeadingText('sustainabilityPoints')}`
              : ''}
          </div>
        </div>
        {descriptionText && (
          <div className={style.categoryDescription}>{descriptionText}</div>
        )}
        {!isLast && <div className={style.categorySeparator}></div>}
      </div>
    );
  };

  const toggleSustainabilityContent = () => {
    setExpandSustainability(!expandSustainability);
  };

  const sustainabilityCardHeader = () => {
    return (
      <div className={style.cardHeaderWithLearnMore}>
        <span>{`${getHeadingText('sustainability')}: ${
          productData?.sustainability?.nachhaltigkeitsScore ||
          calculateTotalScore()
        } / 100 ${getHeadingText('sustainabilityPoints')}`}</span>

        <div
          className={style.learnMoreBtn}
          onClick={toggleSustainabilityContent}
        >
          <span>{getHeadingText('learnMore')}</span>
          <svg
            className={`${style.arrowIcon} ${
              expandSustainability ? style.expanded : ''
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="rgba(0, 0, 0, 0.53)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <>
      <nav className={style.nav}>
        <div className={style.navInner}>
          <img
            src={mainLogo}
            alt="BIOHY"
            style={{ width: '143px', height: '35px' }}
          />
          <div className={style.rightNav}>
            <CustomSelect
              options={countries}
              onChange={handleLanguageChange}
              value={language}
              placeholder="Select"
            />
          </div>
        </div>
      </nav>
      {dataLoaded && productData.productName ? (
        <>
          <header className={style.header}>
            <div className={style.headerInner}>
              <div className={style.leftHeader}>
                {productData.productName}
                {productData.subtitle && (
                  <div className={style.productSubtitle}>
                    {productData.subtitle}
                  </div>
                )}
              </div>
              {/* <a
                href={productData.shopNowLink}
                target="_blank"
                rel="noreferrer"
              >
                <button className={style.headerBtn}>
                  {getHeadingText('shopNow')}
                </button>
              </a> */}
              <div className={style.btnContainer}>
                {productData.shopifyProductDetails?.title && (
                  <div className={style.productItem}>
                    <a
                      href={productData.shopNowLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={productData.shopifyProductDetails?.imageURL}
                        alt=""
                      />

                      {/* <img src={productData.shopifyProductDetails?.imageURL} alt="" /> */}
                      <div className={style.discountPrice}>
                        <p>
                          {' '}
                          {productData.shopifyProductDetails?.compare_at_price}
                          {' €'}
                        </p>
                      </div>

                      <div className={style.actualPrice}>
                        <p>
                          from {productData.shopifyProductDetails?.price} {' €'}
                        </p>
                      </div>

                      <div className={style.title}>
                        <p>{productData.shopifyProductDetails?.title}</p>
                      </div>
                    </a>
                  </div>
                )}

                <a
                  href={productData.shopNowLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <button className={style.headerBtn}>
                    {getHeadingText('shopNow')}
                  </button>
                </a>
              </div>
            </div>
          </header>
          <div className={style.mainWrapper}>
            <div className={style.cards}>
              {productData?.sustainability &&
                (() => {
                  const s = productData.sustainability;
                  const categories = [
                    {
                      key: 'inhaltsstoffe',
                      score: s?.inhaltsstoffe?.score || 0,
                      max: 30,
                      desc: s?.inhaltsstoffe?.descriptions,
                      onClick: () => {
                        window.open(
                          // 'https://biohy-reiniger.de/en/pages/nachhaltigkeitsskala#ingredients',
                          'https://biohy-reiniger.de/pages/nachhaltigkeitsskala#tab-0',
                          '_blank'
                        );
                      },
                    },
                    {
                      key: 'gesundheitUndSicherheit',
                      score: s?.gesundheitUndSicherheit?.score || 0,
                      max: 20,
                      desc: s?.gesundheitUndSicherheit?.descriptions,

                      onClick: () => {
                        window.open(
                          'https://biohy-reiniger.de/pages/nachhaltigkeitsskala#tab-1',
                          '_blank'
                        );
                      },
                    },
                    {
                      key: 'ergiebigkeit',
                      score: s?.ergiebigkeit?.score || 0,
                      max: 20,
                      desc: s?.ergiebigkeit?.descriptions,
                      onClick: () => {
                        window.open(
                          // 'https://biohy-reiniger.de/en/pages/nachhaltigkeitsskala#yield-of-the-cleaners',
                          'https://biohy-reiniger.de/pages/nachhaltigkeitsskala#tab-2',

                          '_blank'
                        );
                      },
                    },
                    {
                      key: 'verpackungUndVersand',
                      score: s?.verpackungUndVersand?.score || 0,
                      max: 15,
                      desc: s?.verpackungUndVersand?.descriptions,
                      onClick: () => {
                        window.open(
                          // 'https://biohy-reiniger.de/en/pages/nachhaltigkeitsskala#packaging-and-shipping',
                          'https://biohy-reiniger.de/pages/nachhaltigkeitsskala#tab-3',

                          '_blank'
                        );
                      },
                    },
                    {
                      key: 'zertifizierungen',
                      score: s?.zertifizierungen?.score || 0,
                      max: 15,
                      desc: s?.zertifizierungen?.descriptions,
                      onClick: () => {
                        window.open(
                          // 'https://biohy-reiniger.de/en/pages/nachhaltigkeitsskala#certifications',
                          'https://biohy-reiniger.de/pages/nachhaltigkeitsskala#tab-4',

                          '_blank'
                        );
                      },
                    },
                  ];
                  const nonZeroCategories = categories.filter(
                    (cat) => cat.score > 0
                  );
                  return (
                    <div ref={sustainabilityRef}>
                      <Card
                        heading={sustainabilityCardHeader()}
                        score={s?.nachhaltigkeitsScore || calculateTotalScore()}
                        maxScore={100}
                        desc={getHeadingText('desc')}
                      >
                        <div
                          className={`${style.sustainabilityContainer} ${
                            expandSustainability
                              ? style.expanded
                              : style.collapsed
                          }`}
                        >
                          {categories.map((cat, index) =>
                            renderSustainabilityCategory(
                              cat.key,
                              cat.score,
                              cat.max,
                              cat.desc,
                              index === categories.length - 1,
                              cat.onClick
                            )
                          )}

                          {productData?.productVideo?.url && (
                            <VideoPlayer
                              videoUrl={productData?.productVideo?.url}
                            />
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                })()}
              <Tabs
                tabs={[
                  {
                    id: 'dosage',
                    label: getHeadingText('applicationDosage'),
                    content: <p>{productData.dosage}</p>,
                  },
                  {
                    id: 'ingredients',
                    label: getHeadingText('ingredients'),
                    content: <p>{productData.ingredients}</p>,
                  },
                  {
                    id: 'safety',
                    label: getHeadingText('safetyWarningsTab'),
                    content: (
                      <>
                        {productData.safetyWarnings && (
                          <p>{productData.safetyWarnings}</p>
                        )}
                        {productData.safetyDataSheet?.url && (
                          <a
                            className={tabsStyle.pdfLink}
                            href={productData.safetyDataSheet.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img src={pdfImage} alt="pdf" />
                            <div className={tabsStyle.pdfLinkText}>
                              <b>{getHeadingText('safetyDataSheet')}</b>
                              <span>{productData.productName}</span>
                            </div>
                          </a>
                        )}
                      </>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </>
      ) : (
        <div className={style.noDataDiv}>
          No data found for the selected language.
        </div>
      )}
      <footer className={style.footer}>
        <div className={style.footerInner}>
          <h2 className={style.footerHeading}>
            {' '}
            {getHeadingText('question') || 'Questions?'}
          </h2>
          <p className={style.footerPara}>
            Melde Dich ganz einfach bei unserem Kundenservice. <br />
            0671 483 254 73 oder info@biohy-reiniger.de
          </p>
        </div>
      </footer>
    </>
  );
};

export default Bio;
